import { ROLE } from "./constants";
import _ from "lodash";
import { documentColors } from "./colors"; // Import the document colors
import moment from "moment";
import i18n from "../i18n";
import { alpha } from '@mui/material/styles';

export const windowWidth = window.innerWidth;
export const windowHeight = window.innerHeight;

export function isMobile() {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent
  );
}

// export const getCitations = (message) => {

//   let citations = [];
//   if (message.role === ROLE.ASSISTANT) {
//     const sub_processes = message.sub_processes;
//     if (sub_processes && sub_processes.length > 0) {
//       const metadata_maps = sub_processes
//         .map((sp) => sp.metadata_map)
//         .filter((m) => !!m);
//       const sub_questions = metadata_maps
//         .map((m) => m?.sub_question)
//         .filter((sq) => !!sq);
//       const uniqueIds = {};
//       const citationsList = sub_questions.map((sq) => sq?.citations || []);
//       citations = citationsList
//         .reduce((acc, curr) => [...acc, ...curr], [])
//         .filter((obj) => {
//           if (!uniqueIds[obj?.file_name]) {
//             uniqueIds[obj?.file_name] = true;
//             return true;
//           }
//           return false;
//         });
//     }
//   }
//   return citations;
// };

// Helper function to get color based on role
// export const getRoleColor = (role) => {
//   switch (role) {
//     case 'viewer':
//       return '#78909c'; // Cool gray
//     case 'editor':
//       return '#66bb6a'; // Soft green
//     case 'reviewer':
//       return '#ba68c8'; // Soft purple
//     case 'creator':
//       return '#42a5f5'; // Sky blue
//     case 'signer':
//       return '#ffa726'; // Vibrant orange
//     default:
//       return '#e0e0e0'; // Light gray
//   }
// };

export const getRoleColor = (role, theme) => {
  switch (role) {
    case 'viewer':
      return theme.palette.text.secondary;
    case 'editor':
      return theme.palette.success.main;
    case 'reviewer':
      return theme.palette.warning.main;
    case 'creator':
      return theme.palette.primary.main;
    case 'signer':
      return theme.palette.secondary.main;
    default:
      return theme.palette.text.secondary;
  }
};


export const getStatusColor = (status, theme) => {
  const statusMap = {
    "SIGNED": theme.palette.success.main,
    "IN_REVIEW": theme.palette.secondary.main,
    "DRAFT": theme.palette.warning.main,
    "PENDING_SIGNATURE": theme.palette.error.main,
  };
  return statusMap[status] || theme.palette.text.disabled;
};

// Helper function for status styling
export const getStatusStyle = (status, theme) => {
  const color = getStatusColor(status, theme);

  return {
    backgroundColor: alpha(color, 0.1),
    color: color,
    fontWeight: 600,
    fontSize: '0.75rem',
    border: `1px solid ${alpha(color, 0.2)}`
  };
};


// Helper function for date formatting
export const formatDate = (dateString, language) => {
  if (!dateString) return '';
  const date = new Date(dateString);

  // Use the language parameter to format date according to user's locale
  return date.toLocaleDateString(language === 'ar' ? 'ar-EG' :
    language === 'fr' ? 'fr-FR' : 'en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};


// Returns the canonical enum key for a status value (enum values are the canonical form)
export const getStatusKey = (status) => status;


export const getCitationSectionTitle = (section_title) => {
  if (!section_title) return '';

  // Special case for "titre"
  if (section_title.toLowerCase() === 'titre') {
    return i18n.t('document');
  }

  // Remove any existing numbers and spaces
  const baseTitle = section_title
    .replace(/_\d+$/, '') // Remove _number at the end
    .replace(/\s+\d+$/, '') // Remove space followed by number at the end
    .replace(':', '')
    .replace('N/A ', '')
    .trim()
    .toLowerCase();

  // Get the section number if it exists
  const numberMatch = section_title.match(/(?:: |\s)(\d+)$/);
  const sectionNumber = numberMatch ? numberMatch[1] : null;

  // Get translation using i18n instance
  const translatedTitle = i18n.t(`section_titles.${baseTitle}`, { defaultValue: baseTitle });

  // Return translated title with number if it exists
  return sectionNumber ? `${translatedTitle} ${sectionNumber}` : translatedTitle;
};

// Format HTML to display nicely in accordions
export function formatHtmlForDisplay(html) {
  if (!html) return '';

  // Replace <br> tags with a bullet points for display
  let text = html.replace(/<br\s*\/?>/gi, ' • ');

  // Remove all other HTML tags
  text = text.replace(/<[^>]*>/g, '');

  // Decode HTML entities
  text = text.replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");

  // Clean up whitespace
  text = text.replace(/\s+/g, ' ').trim();

  return text;
}


export const getToolbarWidth = (canScrollLeft, canScrollRight) => {
  // Base width when both arrows are visible
  const baseWidth = windowWidth / 1.6;

  // Each arrow takes approximately this much space
  const arrowWidth = 30; // Adjust this value based on your actual arrow button width

  if (canScrollLeft && canScrollRight) {
    // Both arrows visible - use base width
    return baseWidth;
  } else if (canScrollLeft || canScrollRight) {
    // Only one arrow visible - add space for one arrow
    return baseWidth + arrowWidth;
  } else {
    // No arrows visible - add space for both arrows
    return baseWidth + (arrowWidth * 2);
  }
};

//////////// //////////////////////////////////////////////// //////////////////////////////////////////////// ////////////////////////////////////
//////////// //////////////////////////////////////////////// //////////////////////////////////////////////// ////////////////////////////////////
//////////// //////////////////////////////////////////////// //////////////////////////////////////////////// ////////////////////////////////////



const parseTableToHTMLSubProcess = (text) => {
  const cleanedText = text.replace(/<br\/?>/g, "\n");
  const lines = cleanedText.trim().split("\n");

  if (lines.length < 2 || !lines[1].match(/^\|[-|]+\|$/)) {
    return null;
  }

  const headers = lines[0]
    .split("|")
    .slice(1, -1)
    .map((header) => header.trim());

  const rows = lines
    .slice(2)
    .filter((line) => line.includes("|"))
    .map((line) =>
      line
        .split("|")
        .slice(1, -1)
        .map((cell) => cell.trim())
    );

  let tableHTML = "<table>";
  tableHTML += "<thead><tr>";
  headers.forEach(
    (header) =>
      (tableHTML += `<th>${header}</th>`)
  );
  tableHTML += "</tr></thead><tbody>";
  rows.forEach((row) => {
    tableHTML += "<tr>";
    row.forEach(
      (cell) =>
        (tableHTML += `<td>${cell}</td>`)
    );
    tableHTML += "</tr>";
  });
  tableHTML += "</tbody></table>";

  return tableHTML;
};

const processMarkdownFormatting = (text) => {
  // Process bold text (**text**)
  let formattedText = text.replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>");
  
  // Process italic text (*text*)
  formattedText = formattedText.replace(/\*(.*?)\*/g, "<em>$1</em>");
  
  // Handle code blocks (`text`)
  formattedText = formattedText.replace(
    /`([^`]+)`/g,
    "<code style='background-color: #f5f5f5; padding: 2px 4px; border-radius: 3px; font-family: monospace;'>$1</code>"
  );
  
  return formattedText;
};

const processListItems = (text) => {
  let processedText = text;
  
  // Debug: Log the text being processed to see what we're working with
  if (text.includes('-') || text.includes('*') || text.includes('+')) {
    console.log('Processing text with potential bullet points:', text);
    console.log('Text length:', text.length);
    console.log('Text contains newlines:', text.includes('\n'));
    console.log('Text contains double newlines:', text.includes('\n\n'));
  }
  
  // Process numbered lists with bold labels (1. **text**: description)
  processedText = processedText.replace(
    /^(\d+)\.\s+\*\*([^*]+)\*\*:\s*(.+)$/gm,
    '<div class="md-numbered-item" style="margin: 1px 0; padding-left: 16px;"><span class="md-number" style="font-weight: 600; color: #666;">$1.</span> <strong>$2:</strong> $3</div>'
  );
  
  // Process numbered lists with bold text but no colon (1. **text** description)
  processedText = processedText.replace(
    /^(\d+)\.\s+\*\*([^*]+)\*\*\s+(.+)$/gm,
    '<div class="md-numbered-item" style="margin: 1px 0; padding-left: 16px;"><span class="md-number" style="font-weight: 600; color: #666;">$1.</span> <strong>$2</strong> $3</div>'
  );
  
  // Process numbered lists without bold labels (1. text)
  processedText = processedText.replace(
    /^(\d+)\.\s+(.+)$/gm,
    '<div class="md-numbered-item" style="margin: 1px 0; padding-left: 16px;"><span class="md-number" style="font-weight: 600; color: #666;">$1.</span> $2</div>'
  );
  
  // Process bullet points with bold labels (- **text**: description)
  processedText = processedText.replace(
    /^[-*+]\s+\*\*([^*]+)\*\*:\s*(.+)$/gm,
    '<div class="md-bullet-item" style="margin: 1px 0; padding-left: 16px;"><span class="md-bullet" style="color: #666;">•</span> <strong>$1:</strong> $2</div>'
  );
  
  // Process bullet points with bold text but no colon (- **text** description)
  processedText = processedText.replace(
    /^[-*+]\s+\*\*([^*]+)\*\*\s+(.+)$/gm,
    '<div class="md-bullet-item" style="margin: 1px 0; padding-left: 16px;"><span class="md-bullet" style="color: #666;">•</span> <strong>$1</strong> $2</div>'
  );
  
  // Process regular bullet points (- text) - improved pattern for French text
  // This is the main pattern that should catch most French bullet points
  processedText = processedText.replace(
    /^[-*+]\s+(.+)$/gm,
    '<div class="md-bullet-item" style="margin: 1px 0; padding-left: 16px;"><span class="md-bullet" style="color: #666;">•</span> $1</div>'
  );
  
  // Process French-style bullet points with minimal spacing after the dash
  // This catches cases like "-Demande de renouvellement" (no space after dash)
  processedText = processedText.replace(
    /^[-*+]([^\n\r]+)$/gm,
    '<div class="md-bullet-item" style="margin: 1px 0; padding-left: 16px;"><span class="md-bullet" style="color: #666;">•</span> $1</div>'
  );
  
  // Process bullet points with optional spaces after the dash
  // This catches cases with varying amounts of whitespace
  processedText = processedText.replace(
    /^[-*+]\s*([^\n\r]+)$/gm,
    '<div class="md-bullet-item" style="margin: 1px 0; padding-left: 16px;"><span class="md-bullet" style="color: #666;">•</span> $1</div>'
  );
  
  // Additional pattern for French text that might have different formatting
  // This handles edge cases where the text might not be properly formatted
  processedText = processedText.replace(
    /^[-*+]\s*([^•\n\r]+?)(?=\n|$)/gm,
    '<div class="md-bullet-item" style="margin: 1px 0; padding-left: 16px;"><span class="md-bullet" style="color: #666;">•</span> $1</div>'
  );
  
  return processedText;
};

const processHeadingsSubProcess = (text) => {
  // Process headings from h6 to h1 (to avoid conflicts)
  let processedText = text;

  // H6 (######)
  processedText = processedText.replace(
    /^#{6}\s+(.+)$/gm,
    (match, content) => {
      const formattedContent = processMarkdownFormatting(content);
      return `<h6 style='margin: 16px 0 8px 0; font-weight: 600; font-size: 1em;'>${formattedContent}</h6>`;
    }
  );

  // H5 (#####)
  processedText = processedText.replace(
    /^#{5}\s+(.+)$/gm,
    (match, content) => {
      const formattedContent = processMarkdownFormatting(content);
      return `<h5 style='margin: 16px 0 8px 0; font-weight: 600; font-size: 1.1em;'>${formattedContent}</h5>`;
    }
  );

  // H4 (####)
  processedText = processedText.replace(
    /^#{4}\s+(.+)$/gm,
    (match, content) => {
      const formattedContent = processMarkdownFormatting(content);
      return `<h4 style='margin: 16px 0 8px 0; font-weight: 600; font-size: 1.2em;'>${formattedContent}</h4>`;
    }
  );

  // H3 (###)
  processedText = processedText.replace(
    /^#{3}\s+(.+)$/gm,
    (match, content) => {
      const formattedContent = processMarkdownFormatting(content);
      return `<h3 style='margin: 16px 0 8px 0; font-weight: 600; font-size: 1.3em;'>${formattedContent}</h3>`;
    }
  );

  // H2 (##)
  processedText = processedText.replace(
    /^#{2}\s+(.+)$/gm,
    (match, content) => {
      const formattedContent = processMarkdownFormatting(content);
      return `<h2 style='margin: 20px 0 10px 0; font-weight: 600; font-size: 1.5em;'>${formattedContent}</h2>`;
    }
  );

  // H1 (#)
  processedText = processedText.replace(
    /^#{1}\s+(.+)$/gm,
    (match, content) => {
      const formattedContent = processMarkdownFormatting(content);
      return `<h1 style='margin: 24px 0 12px 0; font-weight: 600; font-size: 1.8em;'>${formattedContent}</h1>`;
    }
  );

  return processedText;
};

// Fixed version of formatTextToHTMLSubProcess
export const formatTextToHTMLSubProcess = (text) => {
  if (!text) return '';

  // Step 1: Clean and normalize the text
  let cleanedText = text
    .replace(/<br\s*\/?>/gi, "\n") // Convert <br/> tags to newlines
    .replace(/\r\n/g, "\n") // Normalize line endings
    .replace(/\r/g, "\n") // Normalize line endings
    .trim();

  // Step 2: Process the text in sections separated by double newlines
  const sections = cleanedText.split(/\n\s*\n/);
  let htmlContent = "";

  sections.forEach((section) => {
    if (!section.trim()) return;

    // Check if this section contains a table
    const tableHTML = parseTableToHTMLSubProcess(section);
    if (tableHTML) {
      htmlContent += tableHTML;
      return;
    }

    // Process each line in the section
    const lines = section.split('\n').map(line => line.trim()).filter(line => line);
    let processedSection = "";

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      
      // Check for headings first (### Header)
      if (line.match(/^#{1,6}\s+/)) {
        const level = line.match(/^(#{1,6})/)[1].length;
        const content = line.replace(/^#{1,6}\s+/, '');
        const formattedContent = processInlineFormatting(content);
        processedSection += `<h${level} style='margin: ${level <= 2 ? '20px' : '16px'} 0 ${level <= 2 ? '12px' : '8px'} 0; font-weight: 600; font-size: ${2 - (level * 0.1)}em;'>${formattedContent}</h${level}>`;
      }
      // Check for numbered lists (1. item, 2. item, etc.)
      else if (line.match(/^\d+\.\s+/)) {
        const match = line.match(/^(\d+)\.\s+(.+)$/);
        if (match) {
          const number = match[1];
          const content = processInlineFormatting(match[2]);
          processedSection += `<div class="md-numbered-item" style="margin: 6px 0; padding: 8px 12px 8px 36px; position: relative; background: #ffffff; border: 1px solid #e5e7eb; border-radius: 6px; box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04); line-height: 1.5;"><span class="md-number" style="position: absolute; left: 12px; top: 12px; background: #3b82f6; color: white; font-weight: 600; width: 20px; height: 20px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 11px;">${number}</span>${content}</div>`;
        }
      }
      // Check for bullet points (- item, * item, + item)
      else if (line.match(/^\s*[-*+]\s+/)) {
        const content = line.replace(/^\s*[-*+]\s+/, '');
        const formattedContent = processInlineFormatting(content);
        processedSection += `<div class="md-bullet-item" style="margin: 6px 0; padding: 6px 12px 6px 28px; position: relative; background: #ffffff; border: 1px solid #e5e7eb; border-radius: 4px; box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04); line-height: 1.5;"><span class="md-bullet" style="position: absolute; left: 12px; top: 50%; width: 8px; height: 8px; background-color: #059669; border-radius: 50%; transform: translateY(-50%);"></span>${formattedContent}</div>`;
      }
      // Regular paragraphs
      else {
        const formattedContent = processInlineFormatting(line);
        processedSection += `<p style="margin: 8px 0; padding: 0; line-height: 1.6;">${formattedContent}</p>`;
      }
    }

    htmlContent += processedSection;
  });

  // Add CSS styles
  const styles = `
    <style>
      .md-container {
        font-family: 'Nunito', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
        line-height: 1.5;
        color: #333333;
        font-size: 14px;
        margin: 0;
        padding: 0;
      }

      .md-container h1, .md-container h2, .md-container h3, 
      .md-container h4, .md-container h5, .md-container h6 {
        margin-top: 20px;
        margin-bottom: 12px;
        font-weight: 600;
        color: #1a1a1a;
      }

      .md-container h1 { font-size: 1.8em; border-bottom: 2px solid #e5e7eb; padding-bottom: 6px; }
      .md-container h2 { font-size: 1.5em; }
      .md-container h3 { font-size: 1.3em; }
      .md-container h4 { font-size: 1.2em; }
      .md-container h5 { font-size: 1.1em; }
      .md-container h6 { font-size: 1em; }

      .md-container p {
        margin: 8px 0;
        padding: 0;
        line-height: 1.6;
      }

      .md-container .md-numbered-item {
        margin: 6px 0;
        padding: 8px 12px 8px 36px;
        position: relative;
        background: #ffffff;
        border: 1px solid #e5e7eb;
        border-radius: 6px;
        box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
        line-height: 1.5;
        display: block;
      }

      .md-container .md-numbered-item .md-number {
        position: absolute;
        left: 12px;
        top: 12px;
        background: #3b82f6;
        color: white;
        font-weight: 600;
        width: 20px;
        height: 20px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 11px;
      }

      .md-container .md-bullet-item {
        margin: 6px 0;
        padding: 6px 12px 6px 28px;
        position: relative;
        background: #ffffff;
        border: 1px solid #e5e7eb;
        border-radius: 4px;
        box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
        line-height: 1.5;
        display: block;
      }

      .md-container .md-bullet-item .md-bullet {
        position: absolute;
        left: 12px;
        top: 50%;
        width: 8px;
        height: 8px;
        background-color: #059669;
        border-radius: 50%;
        transform: translateY(-50%);
      }

      /* Table styles */
      .md-container table {
        width: 100%;
        border-collapse: collapse;
        margin: 12px 0;
        background: #ffffff;
        border-radius: 8px;
        overflow: hidden;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        border: 1px solid #e5e7eb;
      }

      .md-container table thead {
        background: #f8fafc;
        border-bottom: 2px solid #e5e7eb;
      }

      .md-container table th {
        padding: 12px 16px;
        text-align: left;
        font-weight: 600;
        color: #374151;
        font-size: 0.875rem;
        border-right: 1px solid #e5e7eb;
      }

      .md-container table th:last-child {
        border-right: none;
      }

      .md-container table td {
        padding: 12px 16px;
        border-bottom: 1px solid #f3f4f6;
        border-right: 1px solid #f3f4f6;
        color: #4b5563;
        font-size: 0.875rem;
        line-height: 1.5;
      }

      .md-container table td:last-child {
        border-right: none;
      }

      .md-container table tbody tr:last-child td {
        border-bottom: none;
      }

      .md-container table tbody tr:hover {
        background-color: #f9fafb;
        transition: background-color 0.15s ease;
      }

      /* Mobile optimization */
      @media (max-width: 768px) {
        .md-container {
          font-size: 13px;
        }

        .md-container table {
          font-size: 12px;
        }

        .md-container table th,
        .md-container table td {
          padding: 8px 12px;
        }
      }
    </style>
  `;

  return `${styles}<div class="md-container">${htmlContent}</div>`;
};

// Helper function to process inline formatting (bold, italic, code)
const processInlineFormatting = (text) => {
  return text
    // Bold text (**text**)
    .replace(/\*\*(.*?)\*\*/g, '<strong style="font-weight: 600; color: #1a1a1a;">$1</strong>')
    // Italic text (*text*)
    .replace(/\*([^*]+)\*/g, '<em style="font-style: italic; color: #059669;">$1</em>')
    // Code (`text`)
    .replace(/`([^`]+)`/g, '<code style="background: #f5f5f5; color: #333333; padding: 2px 6px; border-radius: 3px; font-family: monospace; font-size: 0.85rem; border: 1px solid #e0e0e0;">$1</code>');
};




//////////// //////////////////////////////////////////////// //////////////////////////////////////////////// ////////////////////////////////////
//////////// //////////////////////////////////////////////// //////////////////////////////////////////////// ////////////////////////////////////
//////////// //////////////////////////////////////////////// //////////////////////////////////////////////// ////////////////////////////////////

// Modern Markdown to HTML formatter with beautiful design and minimal spacing
export const formatTextToHTML = (markdown) => {
  if (!markdown) return '';
  
  // Clean input
  const cleanText = markdown.trim().replace(/<br\/?>/g, "\n");
  
  // Process markdown step by step
  let html = cleanText
    // Headers (process largest to smallest to avoid conflicts)
    .replace(/^######\s+(.+)$/gm, '<div class="md-heading-6">$1</div>')
    .replace(/^#####\s+(.+)$/gm, '<div class="md-heading-5">$1</div>')
    .replace(/^####\s+(.+)$/gm, '<div class="md-heading-4">$1</div>')
    .replace(/^###\s+(.+)$/gm, '<div class="md-heading-3">$1</div>')
    .replace(/^##\s+(.+)$/gm, '<div class="md-heading-2">$1</div>')
    .replace(/^#\s+(.+)$/gm, '<div class="md-heading-1">$1</div>')
    
    // Bold and italic
    .replace(/\*\*\*(.+?)\*\*\*/g, '<span class="md-bold-italic">$1</span>')
    .replace(/\*\*(.+?)\*\*/g, '<span class="md-bold">$1</span>')
    .replace(/\*(.+?)\*/g, '<span class="md-italic">$1</span>')
    
    // Code
    .replace(/`([^`]+)`/g, '<span class="md-code">$1</span>')
    
    // Horizontal rules
    .replace(/^---+$/gm, '<div class="md-hr"></div>')
    
    // Lists - numbered
    .replace(/^(\d+)\.\s+(.+)$/gm, '<div class="md-numbered-item" data-num="$1">$2</div>')
    
    // Lists - bulleted with bold labels
    .replace(/^[-*+]\s+\*\*([^*]+)\*\*:\s*(.+)$/gm, '<div class="md-bullet-item"><span class="md-bold">$1:</span> $2</div>')
    
    // Lists - regular bullets
    .replace(/^\s*[-*+]\s+(.+)$/gm, '<div class="md-bullet-item">$1</div>')
    
    // Blockquotes
    .replace(/^>\s+(.+)$/gm, '<div class="md-quote">$1</div>')
    
    // Handle line breaks and paragraphs
    .split('\n\n')
    .map(section => {
      const trimmed = section.trim();
      if (!trimmed) return '';
      
      // Check for tables first
      const tableHTML = parseTableToHTMLSubProcess(trimmed);
      if (tableHTML) {
        return tableHTML;
      }
      
      // If it's already a formatted element, keep it
      if (trimmed.match(/^<div class="md-/)) {
        return trimmed;
      }
      
      // Group consecutive numbered items
      if (trimmed.includes('class="md-numbered-item"')) {
        return `<div class="md-numbered-list">${trimmed}</div>`;
      }
      
      // Group consecutive bullet items
      if (trimmed.includes('class="md-bullet-item"')) {
        return `<div class="md-bullet-list">${trimmed}</div>`;
      }
      
      // Regular paragraph
      const withBreaks = trimmed.replace(/\n/g, '<br>');
      return `<div class="md-paragraph">${withBreaks}</div>`;
    })
    .filter(section => section)
    .join('');

  // Completely new CSS with unique class names
  const styles = `
    <style>
      .md-container {
        font-family: 'Nunito', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
        line-height: 1.5;
        color: #333333;
        font-size: 14px;
        margin: 0;
        padding: 0;
        // background-color: #F7FAFC; /* Stylish, clean background */
      }

      /* Headings */
      .md-heading-1 {
        font-size: 1.4rem;
        font-weight: 700;
        color: #1a1a1a;
        margin: 0 0 12px 0;
        padding: 0 0 6px 0;
        border-bottom: 2px solid #e5e7eb;
      }

      .md-heading-2 {
        font-size: 1.2rem;
        font-weight: 600;
        color: #2d2d2d;
        margin: 20px 0 8px 0;
        padding: 0;
      }

      .md-heading-3 {
        font-size: 1.1rem;
        font-weight: 600;
        color: #404040;
        margin: 16px 0 6px 0;
        padding: 0;
      }

      .md-heading-4,
      .md-heading-5,
      .md-heading-6 {
        font-size: 1rem;
        font-weight: 600;
        color: #525252;
        margin: 14px 0 4px 0;
        padding: 0;
      }

      /* Paragraphs */
      .md-paragraph {
        margin: 8px 0;
        padding: 0;
        line-height: 1.6;
      }

      /* Text formatting */
      .md-bold {
        font-weight: 600;
        color: #1a1a1a;
      }

      .md-italic {
        font-style: italic;
        color: #059669; /* Darker green color for italic text */
      }

      .md-bold-italic {
        font-weight: 600;
        font-style: italic;
        color: #1a1a1a;
      }

      .md-code {
        background: #f5f5f5;
        color: #333333;
        padding: 2px 6px;
        border-radius: 3px;
        font-family: 'SF Mono', 'Monaco', 'Consolas', monospace;
        font-size: 0.85rem;
        border: 1px solid #e0e0e0;
      }

      .md-hr {
        border: none;
        height: 1px;
        background: #e5e7eb;
        margin: 20px 0;
      }

      /* Numbered Lists */
      .md-numbered-list {
        margin: 12px 0;
        padding: 0;
      }

      .md-numbered-item {
        background: #ffffff;
        margin: 0 0 3px 0;
        padding: 8px 12px 8px 36px;
        border: 1px solid #e5e7eb;
        border-radius: 6px;
        position: relative;
        box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
        line-height: 1.5;
      }

      .md-numbered-item::before {
        content: attr(data-num);
        position: absolute;
        left: 12px;
        top: 12px;
        background: #3b82f6;
        color: white;
        font-weight: 600;
        width: 20px;
        height: 20px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 11px;
      }

      /* Bullet Lists */
      .md-bullet-list {
        margin: 12px 0;
        padding: 0;
      }

      .md-bullet-item {
        background: #ffffff;
        margin: 0 0 2px 0;
        padding: 6px 12px 6px 28px;
        border: 1px solid #e5e7eb;
        border-radius: 4px;
        position: relative;
        box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
        line-height: 1.5;
      }

      .md-bullet-item::before {
        content: "";
        color: #059669;
        font-size: 22px;
        position: absolute;
        left: 12px;
        top: 50%; /* Center vertically */
        font-weight: bold;
        width: 8px;
        height: 8px;
        background-color: #059669;
        border-radius: 50%;
        display: block;
        transform: translateY(-50%); /* Center the circle vertically */
      }

      /* Blockquotes */
      .md-quote {
        margin: 12px 0;
        padding: 10px 14px;
        background: #f9fafb;
        border-left: 3px solid #6b7280;
        border-radius: 0 4px 4px 0;
        font-style: italic;
        color: #555555;
        line-height: 1.5;
      }

      /* Remove extra spacing from first/last elements */
      .md-container > *:first-child {
        margin-top: 0;
      }

      .md-container > *:last-child {
        margin-bottom: 0;
      }

      /* Compact spacing between specific adjacent elements */
      .md-heading-1 + .md-heading-2,
      .md-heading-2 + .md-heading-3,
      .md-heading-3 + .md-heading-4 {
        margin-top: 8px;
      }

      .md-paragraph + .md-numbered-list,
      .md-paragraph + .md-bullet-list {
        margin-top: 6px;
      }

      .md-numbered-list + .md-paragraph,
      .md-bullet-list + .md-paragraph {
        margin-top: 8px;
      }

      .md-heading-1 + .md-paragraph,
      .md-heading-2 + .md-paragraph,
      .md-heading-3 + .md-paragraph,
      .md-heading-4 + .md-paragraph {
        margin-top: 6px;
      }

      .md-heading-1 + .md-numbered-list,
      .md-heading-2 + .md-numbered-list,
      .md-heading-3 + .md-numbered-list,
      .md-heading-4 + .md-numbered-list,
      .md-heading-1 + .md-bullet-list,
      .md-heading-2 + .md-bullet-list,
      .md-heading-3 + .md-bullet-list,
      .md-heading-4 + .md-bullet-list {
        margin-top: 3px;
      }

      /* Special case: reduce spacing after numbered headings */
      .md-numbered-item + .md-bullet-list {
        margin-top: 3px;
      }

      /* Reduce spacing between numbered items and following bullet items */
      .md-numbered-list + .md-bullet-list {
        margin-top: 3px;
      }

      /* Tables */
      .md-container table {
        width: 100%;
        border-collapse: collapse;
        margin: 12px 0;
        background: #ffffff;
        border-radius: 8px;
        overflow: hidden;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        border: 1px solid #e5e7eb;
      }

      .md-container table thead {
        background: #f8fafc;
        border-bottom: 2px solid #e5e7eb;
      }

      .md-container table th {
        padding: 12px 16px;
        text-align: left;
        font-weight: 600;
        color: #374151;
        font-size: 0.875rem;
        border-right: 1px solid #e5e7eb;
      }

      .md-container table th:last-child {
        border-right: none;
      }

      .md-container table td {
        padding: 12px 16px;
        border-bottom: 1px solid #f3f4f6;
        border-right: 1px solid #f3f4f6;
        color: #4b5563;
        font-size: 0.875rem;
        line-height: 1.5;
      }

      .md-container table td:last-child {
        border-right: none;
      }

      .md-container table tbody tr:last-child td {
        border-bottom: none;
      }

      .md-container table tbody tr:hover {
        background-color: #f9fafb;
        transition: background-color 0.15s ease;
      }

      /* Mobile optimization */
      @media (max-width: 768px) {
        .md-container {
          font-size: 13px;
        }

        .md-heading-1 {
          font-size: 1.3rem;
          margin: 0 0 10px 0;
        }

        .md-heading-2 {
          font-size: 1.1rem;
          margin: 18px 0 6px 0;
        }

        .md-heading-3 {
          font-size: 1rem;
          margin: 14px 0 4px 0;
        }

        .md-paragraph {
          margin: 6px 0;
        }

        .md-numbered-list,
        .md-bullet-list {
          margin: 10px 0;
        }

        .md-numbered-item,
        .md-bullet-item {
          margin: 4px 0;
          padding: 8px 10px 8px 30px;
        }
      }

      /* Improved spacing for better visual flow */
      .md-container > * + * {
        margin-top: 0.5em;
      }

      /* Reduce spacing for consecutive similar elements */
      .md-paragraph + .md-paragraph {
        margin-top: 0.3em;
      }

      .md-numbered-item + .md-numbered-item,
      .md-bullet-item + .md-bullet-item {
        margin-top: 0.2em;
      }

      /* Better spacing for mixed content */
      .md-paragraph + .md-heading-1,
      .md-paragraph + .md-heading-2,
      .md-paragraph + .md-heading-3 {
        margin-top: 1em;
      }

      /* Compact spacing for lists followed by content */
      .md-numbered-list + .md-heading-1,
      .md-numbered-list + .md-heading-2,
      .md-numbered-list + .md-heading-3,
      .md-bullet-list + .md-heading-1,
      .md-bullet-list + .md-heading-2,
      .md-bullet-list + .md-heading-3 {
        margin-top: 0.8em;
      }

      /* Additional improvements for better visual flow */
      .md-container {
        line-height: 1.6;
        word-spacing: 0.05em;
        letter-spacing: 0.01em;
      }

      /* Better spacing for content blocks */
      .md-container > div:not(:last-child) {
        margin-bottom: 0.8em;
      }

      /* Reduce spacing for compact content */
      .md-container .md-paragraph + .md-paragraph {
        margin-top: 0.4em;
      }

      /* Improve list item spacing */
      .md-container .md-numbered-item:not(:last-child),
      .md-container .md-bullet-item:not(:last-child) {
        margin-bottom: 0.5em;
      }

      /* Better table spacing */
      .md-container table + .md-paragraph,
      .md-container table + .md-heading-1,
      .md-container table + .md-heading-2,
      .md-container table + .md-heading-3 {
        margin-top: 0.8em;
      }

      .md-container .md-paragraph + table,
      .md-container .md-heading-1 + table,
      .md-container .md-heading-2 + table,
      .md-container .md-heading-3 + table {
        margin-top: 0.6em;
      }
    </style>
  `;

  return `${styles}<div class="md-container">${html}</div>`;
};





export const translate = (key) => {
  return i18n.t(key);
};

export const docTypes = [
  { label: translate('Décret'), color: '#388e3c' },
  { label: translate('Dahir'), color: '#d32f2f' },
  { label: translate('Arrêté'), color: '#1976d2' },
  { label: translate('Décret-loi'), color: '#0288d1' },
  { label: translate('Décision'), color: '#f57c00' },
  { label: translate('Code'), color: '#8e24aa' },
  { label: translate('Annexe'), color: '#0288d1' },
  { label: translate('Avis'), color: '#ffb300' },
  { label: translate('Instruction'), color: '#43a047' },
  { label: translate('Loi'), color: '#f57c00' },
  { label: translate('Déclaration'), color: '#9c27b0' },
  { label: translate('Article'), color: '#0277bd' },
  { label: translate('Convention'), color: '#0288d1' },
  { label: translate('Contribution'), color: '#795548' },
  { label: translate('Dispositions'), color: '#388e3c' },
  { label: translate('Ordonnance'), color: '#1976d2' },
  { label: translate('Ordre'), color: '#8e24aa' },
  { label: translate('Régime'), color: '#ff7043' },
  { label: translate('Circulaire'), color: '#9c27b0' },
  { label: translate('Incitation'), color: '#ffeb3b' },
  { label: translate('Régularisation'), color: '#43a047' },
  { label: translate('Annulation'), color: '#d32f2f' },
  { label: translate('Liste'), color: '#2196f3' },
  { label: translate('Prime'), color: '#fbc02d' },
  { label: translate('Jurisprudence'), color: '#ff5722' },
];

// Swiss-specific doc_types
export const swissDocTypes = [
  { label: translate('cantonal_constitution'), color: '#8e24aa' },
  { label: translate('laws'), color: '#f57c00' },
  { label: translate('decrees'), color: '#388e3c' },
  { label: translate('ordinances'), color: '#1976d2' },
  { label: translate('treaties'), color: '#0288d1' },
  { label: translate('misc_legislation'), color: '#795548' },
  { label: translate('Jurisprudence'), color: '#ff5722' }
];

// Function to get doc_types based on country
export const getDocTypesByCountry = (country) => {
  if (country === 'CH') {
    return swissDocTypes;
  }
  return docTypes;
};

export const getDocTypeColor = docType => {

  // Normalize the document type to handle both uppercase and lowercase
  const normalizedDocType = docType?.charAt(0).toUpperCase() + docType?.slice(1).toLowerCase();
  
  switch (normalizedDocType) {
    case 'Décret':
      return '#388e3c' // Green
    case 'Dahir':
      return '#d32f2f' // Red
    case 'Arrêté':
      return '#1976d2' // Blue
    case 'Décret-loi':
      return '#0288d1' // Light Blue
    case 'Décision':
      return '#f57c00' // Orange
    case 'Code':
      return '#8e24aa' // Purple
    case 'Annexe':
      return '#0288d1' // Light Blue
    case 'Avis':
      return '#ffb300' // Yellow
    case 'Instruction':
      return '#43a047' // Dark Green
    case 'Loi':
      return '#f57c00' // Orange
    case 'Déclaration':
      return '#9c27b0' // Violet
    case 'Article':
      return '#0277bd' // Dark Blue
    case 'Convention':
      return '#0288d1' // Light Blue
    case 'Contribution':
      return '#795548' // Brown
    case 'Dispositions':
      return '#388e3c' // Green
    case 'Ordonnance':
      return '#1976d2' // Blue
    case 'Ordre':
      return '#8e24aa' // Purple
    case 'Régime':
      return '#ff7043' // Light Red
    case 'Circulaire':
      return '#9c27b0' // Violet
    case 'Incitation':
      return '#ffeb3b' // Yellow
    case 'Régularisation':
      return '#43a047' // Dark Green
    case 'Annulation':
      return '#d32f2f' // Red
    case 'Liste':
      return '#2196f3' // Blue
    case 'Prime':
      return '#fbc02d' // Amber
    case 'Jurisprudence':
      return '#ff5722' // Deep Orange
    // Swiss doc_types
    case 'Cantonal_constitution':
      return '#8e24aa' // Purple
    case 'Laws':
      return '#f57c00' // Orange
    case 'Decrees':
      return '#388e3c' // Green
    case 'Ordinances':
      return '#1976d2' // Blue
    case 'Treaties':
      return '#0288d1' // Light Blue
    case 'Misc_legislation':
      return '#795548' // Brown
    // UAE doc_types
    case 'Constitution':
      return '#8e24aa' // Purple
    case 'Decision':
      return '#f57c00' // Orange
    case 'Decree':
      return '#388e3c' // Green
    case 'Decree-law':
      return '#0288d1' // Light Blue
    case 'Federal-law':
      return '#1565c0' // Indigo
    case 'Gazette':
      return '#78909c' // Grey
    case 'Law':
      return '#1976d2' // Blue
    case 'Legislation':
      return '#5c6bc0' // Indigo
    case 'Regulation':
      return '#00897b' // Teal
    case 'Resolution':
      return '#ef6c00' // Deep Orange
    case 'Treaty':
      return '#0288d1' // Light Blue
    case 'Case-law':
      return '#ff5722' // Deep Orange
    // Moroccan regulation doc types
    case 'Rapport':
      return '#5c6bc0' // Indigo
    case 'Statistiques':
      return '#00897b' // Teal
    case 'Publication':
      return '#78909c' // Blue Grey
    case 'Guide':
      return '#26a69a' // Teal Light
    case 'Étude':
      return '#7e57c2' // Deep Purple
    case 'Directive':
      return '#ef6c00' // Deep Orange
    case 'Note circulaire':
      return '#ab47bc' // Purple Light
    case 'Lettre circulaire':
      return '#8e24aa' // Purple
    case 'Délibération':
      return '#5d4037' // Brown
    default:
      return '#555' // Default color if not found
  }
}





export const getCitations = (message) => {
  let citations = [];

  if (message.role === ROLE.ASSISTANT) {
    const sub_processes = message.sub_processes;

    if (sub_processes && sub_processes.length > 0) {
      const metadata_maps = sub_processes
        .map((sp) => sp.metadata_map)
        .filter((m) => !!m);

      const sub_questions = metadata_maps
        .map((m) => m?.sub_question)
        .filter((sq) => !!sq);

      const uniqueIds = {}; // Dictionary to keep track of unique citations
      const citationsList = sub_questions.map((sq) => sq?.citations || []);

      // Merge all citation lists into one array and remove duplicates
      citations = citationsList
        .reduce((acc, curr) => [...acc, ...curr], [])
        .filter((obj) => {
          const uniqueKey = `${obj?.document_id}_${obj?.section_title}`;
          if (!uniqueIds[uniqueKey]) {
            uniqueIds[uniqueKey] = true;
            return true;
          }
          return false;
        })
        // Note: Sorting removed to preserve backend ordering
        // The backend already provides the correct order: KG nodes first, then "titre" documents, then by score
    }
  }

  return citations;
};

export const formatDateWithTimezoneOffset = (dateString, language) => {
  if (!dateString) return '';
  const date = new Date(dateString);

  // Use the language parameter to format date according to user's locale
  return date.toLocaleDateString(
    language === 'ar' ? 'ar-EG' : language === 'fr' ? 'fr-FR' : 'en-US',
    {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }
  );
};

export const fromBackendDocumentToFrontend = (backendDocuments) => {
  // Sort by 'created_at' so that the de-dupe filter later keeps the oldest duplicate docs
  backendDocuments = _.sortBy(backendDocuments, "created_at");

  let frontendDocs = backendDocuments
    .filter((backendDoc) => "sec_document" in backendDoc.metadata_map)
    .map((backendDoc, index) => {
      // Define the color index, ensuring a maximum of 10 colors
      const colorIndex = index < 10 ? index : 0;

      return {
        id: backendDoc.id,
        url: backendDoc.url,
        short_title: backendDoc.metadata_map.adv_document.short_title,
        long_title: backendDoc.metadata_map.adv_document.long_title,
        file_name: backendDoc.metadata_map.adv_document.file_name,
        new_name: backendDoc.metadata_map.adv_document.new_name,
        date: backendDoc.metadata_map.adv_document.date,
        translated: backendDoc.metadata_map.adv_document.translated,
        ar_pr: backendDoc.metadata_map.adv_document.ar_pr,
        fr_pr: backendDoc.metadata_map.adv_document.fr_pr,
        doc_type: backendDoc.metadata_map.adv_document.doc_type,
        color: documentColors[colorIndex],
        // Add any additional fields like 'quarter' if needed
      };
    });

  // De-duplication logic to avoid duplicate documents based on a custom key
  const getDocDeDupeKey = (doc) => `${doc.ticker}`;

  frontendDocs = _.chain(frontendDocs)
    .sortBy(getDocDeDupeKey)
    .sortedUniqBy(getDocDeDupeKey)
    .value();

  return frontendDocs;
};

// Simple markdown to HTML converter with inline formatting in headers
export const simpleMarkdownToHTML = (markdown, themeMode = 'light') => {
  if (!markdown) return '';

  const isDarkMode = themeMode === 'dark';

  // Helper for inline formatting (bold, italic, code)
  const formatInline = (text) => {
    return text
      // Bold
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      // Italic (avoid matching inside bold)
      .replace(/\*(?!\*)([^*]+)\*/g, '<em>$1</em>')
      // Inline code
      .replace(/`([^`]+)`/g, '<code>$1</code>');
  };

  // Inline styles for headers with increased font sizes
  const h1Style = 'margin-top:1.2em !important;margin-bottom:0.5em !important;padding:0 !important;display:block !important;line-height:1.2 !important;font-size:2em !important;';
  const h2Style = 'margin-top:1.2em !important;margin-bottom:0.5em !important;padding:0 !important;display:block !important;line-height:1.2 !important;font-size:1.6em !important;';
  const h3Style = 'margin-top:1.2em !important;margin-bottom:0.5em !important;padding:0 !important;display:block !important;line-height:1.2 !important;font-size:1.3em !important;';

  // Convert <br/> and <br> to newlines for markdown processing
  let text = markdown.replace(/<br\s*\/?>(\n)?/gi, '\n');

  // Process headers (from h3 to h1) with inline style and increased font size
  text = text
    .replace(/^###\s+(.*)$/gim, (m, p1) => `<h3 style="${h3Style}">${formatInline(p1)}</h3>`)
    .replace(/^##\s+(.*)$/gim, (m, p1) => `<h2 style="${h2Style}">${formatInline(p1)}</h2>`)
    .replace(/^#\s+(.*)$/gim, (m, p1) => `<h1 style="${h1Style}">${formatInline(p1)}</h1>`);

  // Process bold, italic, code outside headers
  text = formatInline(text);

  // Line breaks
  text = text.replace(/\n/g, '<br>');

  // Collapse multiple <br> into one
  text = text.replace(/(<br>\s*){2,}/g, '<br>');

  // Split by double <br> or double newline and wrap in div with clean spacing and increased line height
  text = text.split(/(<br>\s*){2,}/g)
    .filter(Boolean)
    .map(section => `<div style="margin-bottom:0.7em;padding:0;line-height:1.6;">${section.trim()}</div>`)
    .join('');

  // Add beautiful, modern markdown CSS for all elements inside .no-header-spacing
  // Theme-aware colors
  const lightModeStyles = `
    background: #fafbfc;
    box-shadow: 0 2px 8px rgba(60,60,60,0.06);
    color: #23272f;
    border: none;
  `;

  const darkModeStyles = `
    background: #1a1a1a;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
    color: #e8e8e8;
    border: 1px solid #2a2a2a;
  `;

  const forcedStyle = `
    <style>
      .no-header-spacing {
        font-family: 'Inter', 'Nunito', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif;
        ${isDarkMode ? darkModeStyles : lightModeStyles}
        border-radius: 14px;
        padding: 2.2em 1.5em 1.5em 1.5em;
        margin: 0.5em 0;
        max-width: 100%;
      }
      .no-header-spacing h1 {
        color: ${isDarkMode ? '#60a5fa' : '#1a237e'};
        font-weight: 800;
        letter-spacing: -0.01em;
        border-bottom: 2px solid ${isDarkMode ? '#2a2a2a' : '#e3e7ef'};
        padding-bottom: 0.2em;
      }
      .no-header-spacing h2 {
        color: ${isDarkMode ? '#93c5fd' : '#283593'};
        font-weight: 700;
        letter-spacing: -0.01em;
        border-bottom: 1px solid ${isDarkMode ? '#2a2a2a' : '#e3e7ef'};
        padding-bottom: 0.15em;
      }
      .no-header-spacing h3 {
        color: ${isDarkMode ? '#bfdbfe' : '#3949ab'};
        font-weight: 700;
        letter-spacing: -0.01em;
      }
      .no-header-spacing div {
        font-size: 1.08em;
        line-height: 1.7;
        color: ${isDarkMode ? '#e8e8e8' : '#23272f'};
      }
      .no-header-spacing strong {
        font-weight: 700;
        color: ${isDarkMode ? '#f5f5f5' : '#1a1a1a'};
      }
      .no-header-spacing em {
        color: ${isDarkMode ? '#60a5fa' : '#1976d2'};
        font-style: italic;
      }
      .no-header-spacing ul, .no-header-spacing ol {
        margin: 0.7em 0 0.7em 1.2em;
        padding: 0 0 0 1.2em;
      }
      .no-header-spacing ul {
        list-style: disc inside;
      }
      .no-header-spacing ol {
        list-style: decimal inside;
      }
      .no-header-spacing li {
        margin-bottom: 0.3em;
        padding-left: 0.2em;
        color: ${isDarkMode ? '#e8e8e8' : '#23272f'};
      }
      .no-header-spacing blockquote {
        background: ${isDarkMode ? '#242424' : '#f3f6fa'};
        border-left: 4px solid ${isDarkMode ? '#3b82f6' : '#90caf9'};
        margin: 1.2em 0;
        padding: 0.8em 1.2em;
        border-radius: 8px;
        color: ${isDarkMode ? '#b0b0b0' : '#374151'};
        font-style: italic;
      }
      .no-header-spacing code {
        background: ${isDarkMode ? '#242424' : '#f5f7fa'};
        color: ${isDarkMode ? '#60a5fa' : '#c7254e'};
        padding: 0.15em 0.5em;
        border-radius: 5px;
        ${isDarkMode ? 'border: 1px solid #333333;' : ''}
        font-family: 'Fira Mono', 'SFMono-Regular', 'Menlo', 'Monaco', 'Consolas', monospace;
        font-size: 0.98em;
      }
      .no-header-spacing pre {
        background: ${isDarkMode ? '#0f0f0f' : '#f5f7fa'};
        color: ${isDarkMode ? '#e8e8e8' : '#263238'};
        padding: 1em;
        border-radius: 8px;
        ${isDarkMode ? 'border: 1px solid #2a2a2a;' : ''}
        font-family: 'Fira Mono', 'SFMono-Regular', 'Menlo', 'Monaco', 'Consolas', monospace;
        font-size: 1em;
        overflow-x: auto;
        margin: 1.2em 0;
      }

      @media (max-width: 600px) {
        .no-header-spacing {
          padding: 1.2em 0.5em 1em 0.5em;
          font-size: 0.98em;
        }
        .no-header-spacing h1, .no-header-spacing h2, .no-header-spacing h3 {
          font-size: 1.1em !important;
        }
      }
    </style>
  `;

  return `${forcedStyle}<div class="no-header-spacing">${text}</div>`;
};
